import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, ChevronDown, Copy, KeyRound, LogOut, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useV3Prefs } from './V3PrefsContext';
import { useV3Window } from './useV3Window';
import V3Avatar, { avatarCacheKey } from './V3Avatar';
import V3ChangePassword from './V3ChangePassword';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_NICKNAME = 40;

const isImpersonating = () => typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('_impersonated_as');

// Crops the picked picture to a centred square, scales it to 256px and returns it as a JPEG data URL (a few tens of KB).
const squareJpeg = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('That file could not be read'));
  reader.onload = () => {
    const image = new Image();
    image.onerror = () => reject(new Error('That file is not a picture'));
    image.onload = () => {
      const size = 256;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const side = Math.min(image.width, image.height);
      canvas.getContext('2d').drawImage(image, (image.width - side) / 2, (image.height - side) / 2, side, side, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.88));
    };
    image.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
};

const Choice = ({ value, current, onPick, children, testId }) => (
  <button type="button" className={`v3-choice ${current === value ? 'v3-choice--on' : ''}`} aria-pressed={current === value} onClick={() => onPick(value)} data-testid={testId}>
    {children}
  </button>
);

// My preferences: opened from the user circle at the foot of the left bar. A V3 window built like Customer Management (useV3Window), so it sits in the
// content area between the bars and pushes them shut if the room is short. Real name and email are shown but cannot be changed here.
const V3UserPrefs = ({ user, onLogout }) => {
  const { prefsOpen, setPrefsOpen, profile, setProfile, theme, setTheme, defaults, setDefaultState, barMode, setBarMode, saveStatus } = useV3Prefs();
  const win = useV3Window(prefsOpen, 860, { single: true });
  const [nickname, setNickname] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const fileRef = useRef(null);
  const viewingAs = isImpersonating();
  // The Claude MCP connectors this person may see (a super admin gets the C30 Test one and the Live one; anyone a super admin has granted Dashboard Data Access gets the Live one).
  const [connectors, setConnectors] = useState(null);
  const [mcpOpen, setMcpOpen] = useState(false);
  useEffect(() => {
    if (!prefsOpen) return undefined;
    let cancelled = false;
    api.get(`${API}/mcp/my-connectors`)
      .then((response) => { if (!cancelled) setConnectors(response.data); })
      .catch(() => { if (!cancelled) setConnectors(null); });
    return () => { cancelled = true; };
  }, [prefsOpen]);
  const copyUrl = (url, label) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => toast.success(`${label} copied`)).catch(() => toast.error('Could not copy'));
    } else {
      toast.error('Could not copy');
    }
  };

  // Each time the window opens it starts from what is saved.
  useEffect(() => {
    if (!prefsOpen) return;
    setNickname(profile.nickname);

  }, [prefsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!prefsOpen) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setPrefsOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [prefsOpen, setPrefsOpen]);

  if (!prefsOpen || !win.active) return null;

  const saveNickname = async () => {
    const value = nickname.trim();
    if (value === profile.nickname) return;
    setSavingName(true);
    try {
      await api.put(`${API}/auth/profile`, { nickname: value });
      setProfile((previous) => ({ ...previous, nickname: value }));
      setNickname(value);
      toast.success(value ? 'Nickname saved' : 'Nickname removed');
    } catch (error) {
      toast.error(errorText(error, 'Could not save the nickname'));
    } finally {
      setSavingName(false);
    }
  };

  const pickPhoto = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setPhotoBusy(true);
    try {
      const dataUrl = await squareJpeg(file);
      const response = await api.post(`${API}/auth/profile-photo`, { photo: dataUrl });
      const photoId = response.data.photo_id;
      try { sessionStorage.setItem(avatarCacheKey(photoId), dataUrl); } catch (_) { /* private mode */ }
      setProfile((previous) => ({ ...previous, photoId }));
      toast.success('Photo saved');
    } catch (error) {
      toast.error(errorText(error, error?.message || 'Could not save the photo'));
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      await api.delete(`${API}/auth/profile-photo`);
      setProfile((previous) => ({ ...previous, photoId: '' }));
      toast.success('Photo removed');
    } catch (error) {
      toast.error(errorText(error, 'Could not remove the photo'));
    } finally {
      setPhotoBusy(false);
    }
  };

  const realName = user?.full_name || user?.username || '';
  const email = user?.email || user?.username || '';

  return createPortal(
    <div className={`${win.className} v3-user-prefs`} {...win.attrs} style={win.style} data-testid="v3-user-prefs">
      <div role="dialog" aria-modal="true" aria-label="My preferences">
        <div className="v3-modal-bar v3-prefs-head">
          <h2 className="v3-prefs-title">My preferences</h2>
          {/* The colour scheme sits in the middle of the header. */}
          <div className="v3-choices v3-prefs-scheme" role="group" aria-label="Colour scheme">
            <Choice value="light" current={theme} onPick={setTheme} testId="v3-prefs-theme-light">Light</Choice>
            <Choice value="dark" current={theme} onPick={setTheme} testId="v3-prefs-theme-dark">Dark</Choice>
          </div>
          <div className="v3-prefs-head-right">
            {/* Log out lives here now (it used to be in the settings drop-down on the main header). */}
            <button type="button" className="v3-prefs-logout" onClick={() => { setPrefsOpen(false); if (onLogout) onLogout(); }} data-testid="v3-prefs-logout">
              <LogOut className="v3-i v3-i--sm" />Log out
            </button>
            <button type="button" className="v3-prefs-close" onClick={() => setPrefsOpen(false)} aria-label="Close My preferences" data-testid="v3-user-prefs-close">
              <X className="v3-i" />
            </button>
          </div>
        </div>

        <div className="v3-prefs-body">
          {viewingAs && <p className="v3-prefs-note" data-testid="v3-prefs-viewing-as">You are viewing as another person, so their profile and password cannot be changed from here.</p>}

          <section className="v3-prefs-card">
            <h3>Profile</h3>
            <div className="v3-prefs-profile">
              <div className="v3-prefs-photo">
                <V3Avatar user={user} size={96} />
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickPhoto} data-testid="v3-prefs-photo-input" />
                <div className="v3-prefs-photo-actions">
                  <button type="button" className="v3-prefs-btn" disabled={viewingAs || photoBusy} onClick={() => fileRef.current && fileRef.current.click()} data-testid="v3-prefs-photo-upload">
                    <Camera className="v3-i v3-i--sm" />{profile.photoId ? 'Change photo' : 'Upload photo'}
                  </button>
                  {profile.photoId && (
                    <button type="button" className="v3-prefs-btn v3-prefs-btn--quiet" disabled={viewingAs || photoBusy} onClick={removePhoto} data-testid="v3-prefs-photo-remove">
                      <Trash2 className="v3-i v3-i--sm" />Remove
                    </button>
                  )}
                  {/* Change password opens its own window. */}
                  <button type="button" className="v3-prefs-btn" disabled={viewingAs} onClick={() => setShowPassword(true)} data-testid="v3-prefs-change-password">
                    <KeyRound className="v3-i v3-i--sm" />Change password
                  </button>
                </div>
              </div>
              <div className="v3-prefs-fields">
                <label className="v3-prefs-label" htmlFor="v3-prefs-nickname">Nickname</label>
                <div className="v3-prefs-row">
                  <input
                    id="v3-prefs-nickname"
                    className="v3-prefs-input"
                    value={nickname}
                    maxLength={MAX_NICKNAME}
                    disabled={viewingAs}
                    placeholder="Nickname"
                    onChange={(event) => setNickname(event.target.value)}
                    onKeyDown={(event) => { if (event.key === 'Enter') saveNickname(); }}
                    data-testid="v3-prefs-nickname"
                  />
                  <button type="button" className="v3-prefs-btn v3-prefs-btn--primary" disabled={viewingAs || savingName || nickname.trim() === profile.nickname} onClick={saveNickname} data-testid="v3-prefs-nickname-save">
                    Save
                  </button>
                </div>
                <span className="v3-prefs-label">Name</span>
                <div className="v3-prefs-readonly" data-testid="v3-prefs-name">{realName}</div>
                <span className="v3-prefs-label">Email</span>
                <div className="v3-prefs-readonly" data-testid="v3-prefs-email">{email}</div>
                <p className="v3-prefs-hint">Your name and email are set by an administrator and cannot be changed here.</p>
              </div>
            </div>
          </section>

          <section className="v3-prefs-card v3-prefs-card--wide">
            <h3>Side bars</h3>
            <div className="v3-prefs-bar">
              <span className="v3-prefs-label v3-prefs-label--strong">Left bar (navigation)</span>
              <div className="v3-prefs-row v3-prefs-row--between">
                <span className="v3-prefs-label">Starts</span>
                <div className="v3-choices" role="group" aria-label="Left bar (navigation) starts">
                  <Choice value="collapsed" current={defaults.left} onPick={(state) => setDefaultState('left', state)} testId="v3-prefs-left-collapsed">Minimised</Choice>
                  <Choice value="open" current={defaults.left} onPick={(state) => setDefaultState('left', state)} testId="v3-prefs-left-open">Open</Choice>
                </div>
              </div>
              <div className="v3-prefs-row v3-prefs-row--between">
                <span className="v3-prefs-label">Works</span>
                <div className="v3-choices" role="group" aria-label="Left bar (navigation) works">
                  <Choice value="pinned" current={barMode.left} onPick={(mode) => setBarMode('left', mode)} testId="v3-prefs-left-mode-pinned">Pinned</Choice>
                  <Choice value="auto" current={barMode.left} onPick={(mode) => setBarMode('left', mode)} testId="v3-prefs-left-mode-auto">Closes when I click away</Choice>
                  <Choice value="overlay" current={barMode.left} onPick={(mode) => setBarMode('left', mode)} testId="v3-prefs-left-mode-overlay">Over the page</Choice>
                </div>
              </div>
            </div>
            <div className="v3-prefs-bar">
              <span className="v3-prefs-label v3-prefs-label--strong">Right bar (Recents and tasks)</span>
              <div className="v3-prefs-row v3-prefs-row--between">
                <span className="v3-prefs-label">Starts</span>
                <div className="v3-choices" role="group" aria-label="Right bar (Recents and tasks) starts">
                  <Choice value="collapsed" current={defaults.right} onPick={(state) => setDefaultState('right', state)} testId="v3-prefs-right-collapsed">Minimised</Choice>
                  <Choice value="open" current={defaults.right} onPick={(state) => setDefaultState('right', state)} testId="v3-prefs-right-open">Open</Choice>
                </div>
              </div>
              <div className="v3-prefs-row v3-prefs-row--between">
                <span className="v3-prefs-label">Works</span>
                <div className="v3-choices" role="group" aria-label="Right bar (Recents and tasks) works">
                  <Choice value="pinned" current={barMode.right} onPick={(mode) => setBarMode('right', mode)} testId="v3-prefs-right-mode-pinned">Pinned</Choice>
                  <Choice value="auto" current={barMode.right} onPick={(mode) => setBarMode('right', mode)} testId="v3-prefs-right-mode-auto">Closes when I click away</Choice>
                  <Choice value="overlay" current={barMode.right} onPick={(mode) => setBarMode('right', mode)} testId="v3-prefs-right-mode-overlay">Over the page</Choice>
                </div>
              </div>
            </div>
            {saveStatus && saveStatus.state !== 'idle' && (
              <p className={`v3-prefs-hint v3-prefs-save v3-prefs-save--${saveStatus.state}`} role="status" data-testid="v3-prefs-save-status">
                {saveStatus.state === 'saving' && 'Saving your side bar choices…'}
                {saveStatus.state === 'saved' && 'Your side bar choices are saved to your account.'}
                {saveStatus.state === 'failed' && `Not saved to your account (${saveStatus.detail}). Your choice is kept on this device.`}
                {saveStatus.state === 'blocked' && 'You are viewing as another person, so your side bar choices are kept on this device only and are not saved to your account.'}
              </p>
            )}
            <p className="v3-prefs-hint">Starts: how each bar is when you arrive. Works: Pinned stays open and moves the page; Closes when I click away opens as part of the page and folds when you click outside it; Over the page is an icon strip that opens over the page. Pinned and Closes when I click away are also the two buttons at the foot of each bar (the one that is lit minimises the bar and keeps your choice; the other one switches to it). Over the page is chosen here. A page, a window or a small screen can still fold a bar while it is showing, and the arrow on a bar opens or folds it for your visit.</p>
          </section>

          {connectors && (connectors.test || connectors.live) && (
            <section className="v3-prefs-card v3-prefs-card--wide" data-testid="v3-prefs-mcp">
              <button type="button" className="v3-prefs-fold" aria-expanded={mcpOpen} onClick={() => setMcpOpen((open) => !open)} data-testid="v3-prefs-mcp-toggle">
                <h3>MCP connector</h3>
                <ChevronDown className={`v3-i v3-i--sm v3-prefs-fold-icon ${mcpOpen ? 'v3-prefs-fold-icon--open' : ''}`} />
              </button>
              {mcpOpen && (
                <div className="v3-prefs-mcp" data-testid="v3-prefs-mcp-body">
                  {connectors.test && (
                    <div className="v3-prefs-mcp-row">
                      <span className="v3-prefs-label">C30 Test connector (Claude MCP)</span>
                      <div className="v3-prefs-row">
                        <input readOnly className="v3-prefs-input v3-prefs-mono" value={connectors.test.url} onFocus={(event) => event.target.select()} data-testid="v3-prefs-mcp-test-url" />
                        <button type="button" className="v3-prefs-btn" onClick={() => copyUrl(connectors.test.url, 'C30 Test connector URL')} data-testid="v3-prefs-mcp-test-copy"><Copy className="v3-i v3-i--sm" />Copy</button>
                      </div>
                    </div>
                  )}
                  {connectors.live && (
                    <div className="v3-prefs-mcp-row">
                      <span className="v3-prefs-label">Live site connector (Claude MCP)</span>
                      <div className="v3-prefs-row">
                        <input readOnly className="v3-prefs-input v3-prefs-mono" value={connectors.live.url} onFocus={(event) => event.target.select()} data-testid="v3-prefs-mcp-live-url" />
                        <button type="button" className="v3-prefs-btn" onClick={() => copyUrl(connectors.live.url, 'Live site connector URL')} data-testid="v3-prefs-mcp-live-copy"><Copy className="v3-i v3-i--sm" />Copy</button>
                      </div>
                    </div>
                  )}
                  <p className="v3-prefs-hint">Add a URL in Claude as a custom connector, then sign in as yourself and approve it when asked. No key to copy.</p>
                </div>
              )}
            </section>
          )}
          <V3ChangePassword open={showPassword} onClose={() => setShowPassword(false)} />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default V3UserPrefs;
